import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { hasStoredAccessToken } from '@/lib/logto';

/**
 * Auth slice — holds the QLASH user record fetched from `GET /users/me`
 * plus a single source-of-truth `bridgeAuthenticated` flag for the Telegram
 * bridge flow. AppShell, Profile, and Settings all read from here so they
 * stay in sync after sign-in / sign-out.
 *
 * The access token itself is owned by `lib/logto.ts` (manual storage) or by
 * the Logto SDK (standalone web flow) — this slice does not track it.
 */

export interface AuthUser {
  id: number | string;
  username?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  email?: string;
}

interface AuthState {
  user: AuthUser | null;
  /** Mirror of `hasStoredAccessToken()` — drives the AppShell auth gate. */
  bridgeAuthenticated: boolean;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  bridgeAuthenticated: hasStoredAccessToken(),
  status: 'idle',
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.status = 'idle';
      state.error = null;
    },
    setBridgeAuthenticated(state, action: PayloadAction<boolean>) {
      state.bridgeAuthenticated = action.payload;
    },
    setStatus(state, action: PayloadAction<AuthState['status']>) {
      state.status = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.status = action.payload ? 'error' : 'idle';
      state.error = action.payload;
    },
    logout(state) {
      state.user = null;
      state.bridgeAuthenticated = false;
      state.status = 'idle';
      state.error = null;
    },
  },
});

export const { setUser, setBridgeAuthenticated, setStatus, setError, logout } = authSlice.actions;
export default authSlice.reducer;
